import React, { useCallback, useEffect, useState } from 'react';
import { CircularProgress, Snackbar, Alert } from '@mui/material';
import styled from 'styled-components';
import DashboardNavbar from '../dashboardNavbar';
import { SYSTEM_TABS } from '../admin/adminSectionTabs';
import DataTable from '../ui/DataTable';
import { Btn, Tag, StatusBadge, RowBetween, Muted } from '../ui/primitives';
import { fetchCampaigns, setCampaignActive } from '../services/campaignAdminApi';
import CampaignFormDialog from './CampaignFormDialog';
import CampaignOrgAssignDialog from './CampaignOrgAssignDialog';

const PAGE_SIZE = 12;

const TYPE_TONE = { video: 'blue', assessment: 'accent', both: 'green' };
const TYPE_LABEL = { video: 'Video', assessment: 'Assessment', both: 'Video + Assessment' };

/** Super-admin console for creating campaigns and attaching them to organizations. */
export default function CampaignManager() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigning, setAssigning] = useState(null);

  const showToast = (msg, severity = 'success') => setToast({ open: true, msg, severity });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchCampaigns();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load campaigns.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (c) => { setEditing(c); setFormOpen(true); };
  const openAssign = (c) => { setAssigning(c); setAssignOpen(true); };

  const handleToggleActive = async (c) => {
    try {
      await setCampaignActive(c.campaignId, !c.active);
      showToast(`Campaign ${c.active ? 'deactivated' : 'activated'}.`);
      load();
    } catch (e) {
      showToast(e.message || 'Update failed.', 'error');
    }
  };

  const handleSaved = () => {
    setFormOpen(false);
    setEditing(null);
    showToast('Campaign saved.');
    load();
  };

  const handleAssigned = () => {
    setAssignOpen(false);
    setAssigning(null);
    showToast('Organizations updated.');
    load();
  };

  const activeCount = campaigns.filter((c) => c.active).length;

  return (
    <div>
      <DashboardNavbar menuItems={SYSTEM_TABS} />
      <Shell>
        <Inner>
          <PageIntro>
            <RowBetween>
              <div>
                <h2>Campaigns</h2>
                <p>
                  Create campaigns and attach organizations to them. The Flutter app reads the
                  active campaign for an org to show a demo video, an assessment, and a lead form.
                </p>
              </div>
              <Btn onClick={openCreate}>+ New campaign</Btn>
            </RowBetween>
            {!loading && !error && (
              <StatsRow>
                <span><b>{campaigns.length}</b> campaigns</span>
                <span><b>{activeCount}</b> active</span>
              </StatsRow>
            )}
          </PageIntro>

          {loading && (
            <LoadingWrap><CircularProgress size={28} /></LoadingWrap>
          )}

          {!loading && error && (
            <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>
          )}

          {!loading && !error && (
            <DataTable
              rows={campaigns}
              rowKey={(r) => r.campaignId}
              pageSize={PAGE_SIZE}
              searchKeys={['name', 'campaignId', 'type']}
              primary={{
                header: 'Campaign',
                label: (r) => r.name || '(untitled)',
                sub: (r) => r.campaignId,
                short: (r) => (r.name || '?').charAt(0).toUpperCase(),
                sortKey: 'name',
              }}
              columns={[
                {
                  header: 'Type',
                  render: (r) => (
                    <Tag $tone={TYPE_TONE[r.type] || 'gray'}>{TYPE_LABEL[r.type] || r.type || '—'}</Tag>
                  ),
                },
                {
                  header: 'Lead form',
                  render: (r) => (r.isLeadFormEnabled ? <Tag $tone="green">On</Tag> : <Muted>Off</Muted>),
                },
                {
                  header: 'Orgs',
                  width: 90,
                  render: (r) => (
                    <Linkish onClick={() => openAssign(r)} title="Assign organizations">
                      {r.attachedOrgCount || 0}
                    </Linkish>
                  ),
                },
                {
                  header: 'Status',
                  width: 110,
                  render: (r) => (
                    <StatusBadge
                      $active={r.active}
                      onClick={() => handleToggleActive(r)}
                      style={{ cursor: 'pointer' }}
                      title="Click to toggle"
                    >
                      {r.active ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  ),
                },
              ]}
              actions={(r) => (
                <>
                  <Btn $sm $variant="ghost" onClick={() => openEdit(r)}>Edit</Btn>
                  <Btn $sm $variant="ghost" onClick={() => openAssign(r)}>Orgs</Btn>
                </>
              )}
              actionsColumnWidth={150}
              emptyText="No campaigns yet. Create your first one."
            />
          )}
        </Inner>
      </Shell>

      <CampaignFormDialog
        open={formOpen}
        campaign={editing}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSaved={handleSaved}
      />
      <CampaignOrgAssignDialog
        open={assignOpen}
        campaign={assigning}
        onClose={() => { setAssignOpen(false); setAssigning(null); }}
        onSaved={handleAssigned}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </div>
  );
}

const Shell = styled.div`
  padding: 24px 28px 60px;
`;

const Inner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const PageIntro = styled.div`
  margin-bottom: 20px;
  h2 { font-family: ${({ theme }) => theme.fontHead}; font-size: 22px; font-weight: 760; margin: 0 0 4px; letter-spacing: -.01em; }
  p { margin: 0; color: ${({ theme }) => theme.textMuted}; font-size: 13.5px; max-width: 640px; }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 14px;
  font-size: 13px;
  color: ${({ theme }) => theme.textMuted};
  b { color: ${({ theme }) => theme.text}; }
`;

const LoadingWrap = styled.div`
  display: grid;
  place-items: center;
  padding: 48px 0;
`;

const Linkish = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 650;
  color: ${({ theme }) => theme.accentStrong};
  &:hover { text-decoration: underline; }
`;
